/**
 * Response Validator for PinPad Operations
 * 
 * Validates CDP response structures and data types.
 * Each operation has a specific schema for validation.
 * Ensures data integrity before processing responses.
 * 
 * @module utilities/pinpad-v2/validator
 */

import { parseError, createError } from '../../config/pinpad-v2/error-codes';

/**
 * Response schemas for each operation
 */
const RESPONSE_SCHEMAS = {
  CONNECT: {
    required: ['success', 'message'],
    optional: ['version', 'terminalId'],
    types: {
      success: 'boolean',
      message: 'string',
      version: 'string',
      terminalId: 'string'
    }
  },

  LOGIN: {
    required: ['success', 'message', 'sessionId'],
    optional: ['user', 'timestamp'],
    types: {
      success: 'boolean',
      message: 'string',
      sessionId: 'string',
      user: 'string',
      timestamp: 'number'
    }
  },

  GET_KEYS: {
    required: ['success', 'publicKey'],
    optional: ['keyId', 'algorithm'],
    types: {
      success: 'boolean',
      publicKey: 'string',
      keyId: 'string',
      algorithm: 'string'
    }
  },

  READ_CARD: {
    required: ['success', 'cardNumber', 'cardType'],
    optional: ['bin', 'last4', 'expirationDate', 'cardholderName', 'track2'],
    types: {
      success: 'boolean',
      cardNumber: 'string',
      cardType: 'string',
      bin: 'string',
      last4: 'string',
      expirationDate: 'string',
      cardholderName: 'string',
      track2: 'string'
    }
  },

  GET_MERCHANT: {
    required: ['success', 'merchantId', 'merchantName'],
    optional: ['affiliation', 'terminalId', 'address'],
    types: {
      success: 'boolean',
      merchantId: 'string',
      merchantName: 'string',
      affiliation: 'string',
      terminalId: 'string',
      address: 'string'
    }
  },

  VENTA: {
    required: ['success', 'authorizationCode', 'referenceNumber', 'transactionId'],
    optional: [
      'amount',
      'cardType',
      'last4',
      'timestamp',
      'voucher',
      'responseCode',
      'responseMessage',
      'batchNumber',
      'terminalId',
      'merchantId'
    ],
    types: {
      success: 'boolean',
      authorizationCode: 'string',
      referenceNumber: 'string',
      transactionId: 'string',
      amount: 'number',
      cardType: 'string',
      last4: 'string',
      timestamp: 'number',
      voucher: 'string',
      responseCode: 'string',
      responseMessage: 'string',
      batchNumber: 'string',
      terminalId: 'string',
      merchantId: 'string'
    }
  },

  REIMPRESION: {
    required: ['success', 'voucher'],
    optional: ['referenceNumber', 'timestamp'],
    types: {
      success: 'boolean',
      voucher: 'string',
      referenceNumber: 'string',
      timestamp: 'number'
    }
  }
};

/**
 * Validation result
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - Whether validation passed
 * @property {Array<string>} errors - Array of validation error messages
 * @property {Object} data - Validated data (if valid)
 */

/**
 * Check if value matches expected type
 * @param {any} value - Value to check
 * @param {string} expectedType - Expected type name
 * @returns {boolean} True if type matches
 */
function isValidType(value, expectedType) {
  if (value === null || value === undefined) {
    return false;
  }

  const actualType = typeof value;

  switch (expectedType) {
    case 'string':
      return actualType === 'string' && value.length > 0;
    case 'number':
      return actualType === 'number' && !isNaN(value);
    case 'boolean':
      return actualType === 'boolean';
    case 'object':
      return actualType === 'object' && !Array.isArray(value);
    case 'array':
      return Array.isArray(value);
    default:
      return false;
  }
}

/**
 * Validate response against schema
 * @param {Object} response - Response object to validate
 * @param {string} operation - Operation name
 * @returns {ValidationResult} Validation result
 */
export function validateResponse(response, operation) {
  const errors = [];

  // Check if response exists
  if (!response || typeof response !== 'object') {
    return {
      valid: false,
      errors: ['Response is null or not an object'],
      data: null
    };
  }

  // Get schema for operation
  const schema = RESPONSE_SCHEMAS[operation.toUpperCase()];
  
  if (!schema) {
    console.warn(`[Validator] No schema defined for operation: ${operation}`);
    return {
      valid: true,
      errors: [],
      data: response
    };
  }

  // Check required fields
  for (const field of schema.required) {
    if (!(field in response)) {
      errors.push(`Missing required field: ${field}`);
      continue;
    }

    // Validate type
    const expectedType = schema.types[field];
    if (expectedType && !isValidType(response[field], expectedType)) {
      errors.push(
        `Invalid type for field '${field}': expected ${expectedType}, got ${typeof response[field]}`
      );
    }
  }

  // Validate optional fields if present
  for (const field of schema.optional) {
    if (field in response) {
      const expectedType = schema.types[field];
      if (expectedType && !isValidType(response[field], expectedType)) {
        errors.push(
          `Invalid type for field '${field}': expected ${expectedType}, got ${typeof response[field]}`
        );
      }
    }
  }

  // Check for error responses
  if (response.error || response.success === false) {
    const parsedError = parseError(response);
    errors.push(`Operation failed: ${parsedError.message}`);
  }

  return {
    valid: errors.length === 0,
    errors,
    data: errors.length === 0 ? response : null
  };
}

/**
 * Validate and throw if invalid
 * @param {Object} response - Response object to validate
 * @param {string} operation - Operation name
 * @returns {Object} Validated response
 * @throws {Error} If validation fails
 */
export function validateAndThrow(response, operation) {
  const result = validateResponse(response, operation);

  if (!result.valid) {
    const error = createError('0306', {
      operation,
      validationErrors: result.errors,
      response
    });
    error.message = `Validation failed for ${operation}: ${result.errors.join(', ')}`;
    throw error;
  }

  return result.data;
}

/**
 * Validate card number format
 * @param {string} cardNumber - Card number to validate
 * @returns {boolean} True if valid
 */
export function isValidCardNumber(cardNumber) {
  if (!cardNumber || typeof cardNumber !== 'string') {
    return false;
  }

  // Remove spaces and dashes
  const cleaned = cardNumber.replace(/[\s-]/g, '');

  // Check if all digits
  if (!/^\d+$/.test(cleaned)) {
    return false;
  }

  // Check length (13-19 digits)
  if (cleaned.length < 13 || cleaned.length > 19) {
    return false;
  }

  // Luhn algorithm validation
  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

/**
 * Validate amount format
 * @param {number} amount - Amount to validate
 * @returns {boolean} True if valid
 */
export function isValidAmount(amount) {
  if (typeof amount !== 'number') {
    return false;
  }

  // Check if positive
  if (amount <= 0) {
    return false;
  }

  // Check if reasonable (not too large)
  if (amount > 999999.99) {
    return false;
  }

  // Check if has max 2 decimal places
  const decimals = (amount.toString().split('.')[1] || '').length;
  return decimals <= 2;
}

/**
 * Validate reference number format
 * @param {string} reference - Reference number to validate
 * @returns {boolean} True if valid
 */
export function isValidReference(reference) {
  if (!reference || typeof reference !== 'string') {
    return false;
  }

  // Check length (minimum 5, maximum 50 characters)
  if (reference.length < 5 || reference.length > 50) {
    return false;
  }

  // Check if alphanumeric with allowed special chars
  return /^[a-zA-Z0-9\-_]+$/.test(reference);
}

/**
 * Validate authorization code format
 * @param {string} authCode - Authorization code to validate
 * @returns {boolean} True if valid
 */
export function isValidAuthCode(authCode) {
  if (!authCode || typeof authCode !== 'string') {
    return false;
  }

  // Check length (typically 6 digits)
  if (authCode.length < 4 || authCode.length > 10) {
    return false;
  }

  // Check if alphanumeric
  return /^[a-zA-Z0-9]+$/.test(authCode);
}

/**
 * Sanitize response data (remove sensitive information for logging)
 * @param {Object} response - Response object
 * @returns {Object} Sanitized response
 */
export function sanitizeResponse(response) {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const sanitized = { ...response };

  // Mask sensitive fields
  const sensitiveFields = [
    'cardNumber',
    'track2',
    'pin',
    'cvv',
    'password',
    'publicKey',
    'privateKey'
  ];

  for (const field of sensitiveFields) {
    if (field in sanitized && sanitized[field]) {
      const value = sanitized[field].toString();
      
      if (field === 'cardNumber' && value.length >= 6) {
        // Show first 6 and last 4
        sanitized[field] = value.substring(0, 6) + '****' + value.substring(value.length - 4);
      } else if (field === 'publicKey' && value.length > 20) {
        // Show first and last 10 chars
        sanitized[field] = value.substring(0, 10) + '...' + value.substring(value.length - 10);
      } else {
        sanitized[field] = '***MASKED***';
      }
    }
  }

  return sanitized;
}

/**
 * Validate transaction data before sending
 * @param {Object} transactionData - Transaction data to validate
 * @returns {ValidationResult} Validation result
 */
export function validateTransaction(transactionData) {
  const errors = [];

  // Required fields
  if (!transactionData.amount || !isValidAmount(transactionData.amount)) {
    errors.push('Invalid or missing amount');
  }

  if (!transactionData.reference || !isValidReference(transactionData.reference)) {
    errors.push('Invalid or missing reference');
  }

  // Optional but validated if present
  if (transactionData.cardNumber && !isValidCardNumber(transactionData.cardNumber)) {
    errors.push('Invalid card number format');
  }

  return {
    valid: errors.length === 0,
    errors,
    data: errors.length === 0 ? transactionData : null
  };
}

/**
 * Get schema for operation (for testing/debugging)
 * @param {string} operation - Operation name
 * @returns {Object|null} Schema or null if not found
 */
export function getSchema(operation) {
  return RESPONSE_SCHEMAS[operation.toUpperCase()] || null;
}

/**
 * Get all schemas (for testing/debugging)
 * @returns {Object} All response schemas
 */
export function getAllSchemas() {
  return { ...RESPONSE_SCHEMAS };
}

export default {
  validateResponse,
  validateAndThrow,
  isValidCardNumber,
  isValidAmount,
  isValidReference,
  isValidAuthCode,
  sanitizeResponse,
  validateTransaction,
  getSchema,
  getAllSchemas
};
